/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { ThunkAction } from 'model/store/thunk'
import RequestAction from 'store/RequestAction'
import { EmailTemplate } from 'types/entities/EmailTemplate'
import { pushTemplate, removeTemplate, setTemplatesForModel } from './EmailTemplatesReducer'
import { EmailExportUtils } from 'utils/email/EmailExportUtils'

export class EmailTemplatesAction {
    static fetchCreateOrUpdateTemplate = (
        data: Partial<EmailTemplate>,
        files?: File[]
    ): ThunkAction<Promise<EmailTemplate>> => {
        return async (dispatch, getState): Promise<EmailTemplate> => {
            const state = getState()
            const creatorId = state.sessionState.session?.user?.id

            const formData = new FormData()
            if (data.id) formData.append('id', String(data.id))
            formData.append('name', data.name || 'Email Template')
            formData.append('type', 'mail')
            if (creatorId) formData.append('creator_id', String(creatorId))

            // Append generic data fields for model association
            formData.append('data[model_name]', data.model_name || 'Offer')
            formData.append('data[model_id]', String(data.model_id))
            formData.append('data[subject]', data.subject || '')
            formData.append('data[content]', data.content || '')
            if (data.category) formData.append('data[category]', data.category)

            // Append files to be sent as part of multipart/form-data
            if (files && files.length > 0) {
                files.forEach((file) => {
                    formData.append('files[]', file)
                })
            }

            const response = await RequestAction.makeAuthenticatedRequest<{
                importTemplate: {
                    id: number
                    name: string
                    type: string
                    data: {
                        subject: string
                        content: string
                        model_id: string
                        model_name?: string
                        category?: string
                    }
                    user_id: number
                }
            }>(getState, '/admin/import/create-mail-template', formData, 'post')

            if (response?.importTemplate) {
                const apiTemplate = response.importTemplate
                const mappedTemplate: EmailTemplate = {
                    id: apiTemplate.id,
                    model_id: Number(apiTemplate.data.model_id),
                    model_name: apiTemplate.data.model_name || 'Offer',
                    name: apiTemplate.name,
                    subject: apiTemplate.data.subject,
                    content: apiTemplate.data.content,
                    category: apiTemplate.data.category,
                }
                dispatch(pushTemplate(mappedTemplate))
                return mappedTemplate
            }

            return Promise.reject(response)
        }
    }

    static fetchDeleteTemplate = (template: EmailTemplate): ThunkAction<Promise<void>> => {
        return async (dispatch, getState): Promise<void> => {
            try {
                await RequestAction.makeAuthenticatedRequest<any>(
                    getState,
                    `/admin/offers/email-templates/delete/${template.id}`,
                    undefined,
                    'delete'
                )
                // If we get here, the request succeeded (AxiosCommon would have rejected on error)
                dispatch(
                    removeTemplate({
                        modelName: template.model_name,
                        modelId: template.model_id,
                        templateId: template.id,
                    })
                )
                // Refresh the list and wait for it to complete
                await dispatch(EmailTemplatesAction.fetchTemplates(template.model_name, template.model_id))
            } catch (error) {
                return Promise.reject(error)
            }
        }
    }

    static fetchTemplates = (modelName: string, modelId: number): ThunkAction<Promise<EmailTemplate[]>> => {
        return async (dispatch, getState): Promise<EmailTemplate[]> => {
            interface ApiTemplate {
                id: number
                name: string
                user_id: number
                data: {
                    subject: string
                    content: string
                    model_id: string
                    model_name?: string
                    category?: string
                }
                created_at: string | null
                updated_at: string | null
            }

            interface ApiTemplateData {
                importMailTemplates: ApiTemplate[]
            }

            // The original endpoint was /admin/offers/email-templates/list
            // We assume the backend can handle filtering or we use the same endpoint and filter here
            const response = await RequestAction.makeAuthenticatedRequest<ApiTemplateData>(
                getState,
                '/admin/offers/email-templates/list',
                undefined,
                'get'
            )

            if (response && response.importMailTemplates) {
                const mappedTemplates: EmailTemplate[] = response.importMailTemplates
                    .filter((t) => {
                        const tModelId = Number(t.data.model_id)
                        const tModelName = t.data.model_name || 'Offer'
                        return tModelId === modelId && tModelName === modelName
                    })
                    .map((t) => ({
                        id: t.id,
                        model_id: Number(t.data.model_id),
                        model_name: t.data.model_name || 'Offer',
                        name: t.name,
                        subject: t.data.subject,
                        content: t.data.content,
                        category: t.data.category,
                        created_at: t.created_at || undefined,
                        updated_at: t.updated_at || undefined,
                    }))

                dispatch(
                    setTemplatesForModel({
                        modelName,
                        modelId,
                        templates: mappedTemplates,
                    })
                )
                return mappedTemplates
            }

            return Promise.reject(response)
        }
    }

    static exportToEml = async (
        template: EmailTemplate,
        placeholders: { [key: string]: string },
        attachments: File[] = []
    ) => {
        await EmailExportUtils.exportToEml(template, placeholders, attachments)
    }
}
